import React, { useState, useEffect } from "react";
import { Box } from "@mui/material";
import { LinkOff as UnlinkIcon } from "@mui/icons-material";
import { dt } from "../../../../shared/themes/redesignTokens";
import { EnrichedServiceReport } from "../../store/serviceReportsSlice";
import serviceReportsApi from "../../api/serviceReportsApi";

// ─── Props ──────────────────────────────────────────────────

interface BulkUnlinkDialogProps {
  open: boolean;
  items: EnrichedServiceReport[];
  onClose: () => void;
  onUnlinked: (count: number) => void;
  translate: (key: string) => string;
}

// ─── Component ──────────────────────────────────────────────

const BulkUnlinkDialog: React.FC<BulkUnlinkDialogProps> = ({
  open,
  items,
  onClose,
  onUnlinked,
  translate,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setError("");
      setSubmitting(false);
      setProgress(0);
    }
  }, [open]);

  // Inject keyframes
  useEffect(() => {
    const id = "bulk-unlink-dialog-keyframes";
    if (!document.getElementById(id)) {
      const style = document.createElement("style");
      style.id = id;
      style.textContent = `
        @keyframes bulkUnlinkSlideUp {
          0% { opacity: 0; transform: translateY(24px) scale(0.97); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes bulkUnlinkBackdropIn {
          0% { opacity: 0; }
          100% { opacity: 1; }
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  const handleSubmit = async () => {
    if (submitting) return;
    setSubmitting(true);
    setError("");

    let unlinked = 0;
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const wo = item.linkedWo;
      if (!wo || !item.sr.id) continue;
      setProgress(i + 1);
      try {
        await serviceReportsApi.unlinkSrFromWorkOrder(wo.id, item.sr.id);
        unlinked++;
      } catch {
        // Continue with others even if one fails
      }
    }

    if (unlinked === 0) {
      setError(translate("serviceReports.bulk.unlinkFailedAll"));
      setSubmitting(false);
    } else {
      onUnlinked(unlinked);
    }
  };

  if (!open) return null;

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 1400,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {/* Backdrop */}
      <Box
        onClick={submitting ? undefined : onClose}
        sx={{
          position: "absolute",
          inset: 0,
          backgroundColor: "rgba(12,17,23,0.25)",
          animation: "bulkUnlinkBackdropIn 0.2s ease",
        }}
      />

      {/* Dialog */}
      <Box
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: "420px",
          mx: "16px",
          backgroundColor: "#FFFFFF",
          borderRadius: "20px",
          overflow: "hidden",
          boxShadow: "0 20px 60px rgba(0,0,0,0.12), 0 4px 16px rgba(0,0,0,0.06)",
          animation: "bulkUnlinkSlideUp 0.3s ease",
        }}
      >
        {/* Muted accent bar */}
        <Box
          sx={{
            height: "4px",
            background: "linear-gradient(90deg, #6B7280 0%, #9CA3AF 100%)",
          }}
        />

        {/* Content */}
        <Box sx={{ padding: "32px 28px 24px", textAlign: "center" }}>
          {/* Icon */}
          <Box
            sx={{
              width: "48px",
              height: "48px",
              borderRadius: "50%",
              backgroundColor: "rgba(107,114,128,0.10)",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              mb: "16px",
            }}
          >
            <UnlinkIcon sx={{ fontSize: "24px", color: "#6B7280" }} />
          </Box>

          {/* Title */}
          <Box
            sx={{
              fontFamily: dt.font,
              fontSize: "18px",
              fontWeight: 700,
              color: "#0C1117",
              mb: "12px",
            }}
          >
            {translate("serviceReports.bulk.unlinkTitle").replace("{{count}}", String(items.length))}
          </Box>

          {/* Description */}
          <Box
            sx={{
              fontFamily: dt.font,
              fontSize: "13px",
              color: "#6B7280",
              lineHeight: 1.6,
              mb: "20px",
            }}
          >
            {translate("serviceReports.bulk.unlinkDescription")}
          </Box>

          {/* Error message */}
          {error && (
            <Box
              sx={{
                fontFamily: dt.font,
                fontSize: "12px",
                color: "#DC2626",
                backgroundColor: "rgba(220,38,38,0.06)",
                borderRadius: "8px",
                padding: "8px 12px",
                mb: "16px",
              }}
            >
              {error}
            </Box>
          )}

          {/* Progress indicator */}
          {submitting && (
            <Box
              sx={{
                fontFamily: dt.font,
                fontSize: "13px",
                fontWeight: 500,
                color: "#6B7280",
                textAlign: "center",
                mb: "16px",
              }}
            >
              {translate("serviceReports.bulk.unlinkingProgress")
                .replace("{{current}}", String(progress))
                .replace("{{total}}", String(items.length))}
            </Box>
          )}

          {/* Buttons */}
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "16px", mt: error || submitting ? "0px" : "4px" }}>
            <Box
              onClick={submitting ? undefined : onClose}
              sx={{
                fontFamily: dt.font,
                fontSize: "13px",
                fontWeight: 500,
                color: "#8C9099",
                cursor: submitting ? "default" : "pointer",
                padding: "8px 16px",
                borderRadius: "8px",
                transition: "color 0.15s ease",
                opacity: submitting ? 0.5 : 1,
                "&:hover": submitting ? {} : { color: "#4A4F57" },
              }}
            >
              {translate("serviceReports.actions.cancel")}
            </Box>
            <Box
              onClick={handleSubmit}
              sx={{
                fontFamily: dt.font,
                fontSize: "13px",
                fontWeight: 600,
                color: "#6B7280",
                border: submitting ? "1.5px solid #D1CCC4" : "1.5px solid #9CA3AF",
                backgroundColor: "transparent",
                borderRadius: "24px",
                padding: "9px 28px",
                cursor: submitting ? "default" : "pointer",
                transition: "all 0.2s ease",
                opacity: submitting ? 0.6 : 1,
                "&:hover": submitting
                  ? {}
                  : { backgroundColor: "rgba(107,114,128,0.06)" },
              }}
            >
              {submitting
                ? translate("serviceReports.bulk.unlinking")
                : translate("serviceReports.bulk.unlinkButton")}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default BulkUnlinkDialog;
